/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isError from 'lodash/isError';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';

import PlatformUtil from '../../platform/platform-util';
import EnvUtil from '../env-util';
import UpdatePlatformUtil from '../update/update-platform-util';

import ErrorLevel from '../error/error-level';

// TODO Chybí unit testy:
// - Původní myšlenka byla taková, že se pro testy zachová originální
//   Sentry knihovna a bude se mockovat fetch, jako výstup z této knihovny.
//   To ale bohužel není možné, protože Sentry nedovoluje opakovanou
//   inicializaci, stejně jako nepodporuje zrušení již inicializované instance.
//   Problémy s opakovanou inicializací se týkají např. nevyvolání metody
//   `setupOnce` v integracích.
//   V kombinaci naslouchání naudálosti `error` u `window` a další,
//   není vhodné tuto knihovnu inicializovat v rámci unit testů.
// - Další variantou je tedy vytvoření mocku Sentry API.
/**
 * Zaznamenávač výjimek pro Sentry API.
 * Odesílá chyby na Sentry server.
 * @implements {Logger}
 */
class SentryLogger {
  /**
   * Vytvoří instanci.
   * @param {SentryBrowser} Sentry
   * @param {ErrorLevel} captureErrorLevel Úroveň důležitosti chyby,
   * od které se začínají odesílat události na Sentry.
   * @param {string} dsn Sentry DSN adresa k odesílání záznamů.
   * @param {ErrorLevelFactory} errorLevelFactory
   * @param {SentryOfflineIntegration} offlineIntegration
   * @param {SentrySeverityFactory} severityFactory
   * @param {SentryVueIntegration} vueIntegration
   */
  constructor(
    Sentry,
    captureErrorLevel,
    dsn,
    errorLevelFactory,
    offlineIntegration,
    severityFactory,
    vueIntegration
  ) {
    // TODO Sentry integrace umějí fungovat pouze s výchozím hubem.
    // - Není tedy možné používat více instancí zýroveň.
    // - https://github.com/getsentry/sentry-javascript/issues/2389
    Sentry.init({
      dsn,
      integrations: (integrations) => [
        ...integrations,
        vueIntegration,
        offlineIntegration
      ]
    });
    offlineIntegration.sendEvents();
    const hub = Sentry.getCurrentHub();
    const client = hub.getClient();
    this.captureErrorLevel = captureErrorLevel;
    this.client = client;
    this.errorLevelFactory = errorLevelFactory;
    this.hub = hub;
    this.severityFactory = severityFactory;
  }

  /**
   * Zaznamená libovolnou hodnotu,
   * přičemž se hodnota úrovně chyby použije primárně z této hodnoty
   * a sekundárně z názvu metody zanamenávače chyb,
   * přes kterou byla chyba předána.
   * @param {ErrorLevel} methodLevel Úroveň chyby, která přísluší metodě,
   * která byla na zaznamenávači výjimek volána.
   * @param {*} value
   * @private
   * @returns {undefined}
   */
  capture(methodLevel, value) {
    const valueLevel = this.errorLevelFactory.fromValue(value);
    const level = valueLevel || methodLevel;
    if (isError(value)) {
      return this.captureError(level, value);
    }
    if (isObject(value)) {
      return this.captureObject(level, value);
    }
    return this.captureString(level, String(value));
  }

  /**
   * Zaznamená chybový objekt s požadovanou úrovní důležitosti.
   * @param {ErrorLevel} level
   * @param {Error} error
   * @private
   * @returns {undefined}
   */
  captureError(level, error) {
    const { hub } = this;
    const severity = this.severityFactory.fromErrorLevel(level);
    if (this.isBreadcrumb(level)) {
      hub.addBreadcrumb({
        message: error.toString(),
        level: severity
      });
      return;
    }
    hub.withScope((scope) => {
      scope.setLevel(severity);
      hub.captureException(error);
    });
  }

  /**
   * Zaznamená obecný objekt s požadovanou úrovní důležitosti.
   * @param {ErrorLevel} level
   * @param {Object} object
   * @private
   * @returns {undefined}
   */
  captureObject(level, object) {
    const { message, name } = object;
    if (!isString(message)) {
      return this.captureString(level, String(object));
    }
    if (!isString(name)) {
      return this.captureString(level, message);
    }
    return this.captureString(level, `${name}: ${message}`);
  }

  /**
   * Zaznamená řetězec s požadovanou úrovní důležitosti.
   * @param {ErrorLevel} level
   * @param {string} string
   * @private
   * @returns {undefined}
   */
  captureString(level, string) {
    const { hub } = this;
    const severity = this.severityFactory.fromErrorLevel(level);
    if (this.isBreadcrumb(level)) {
      hub.addBreadcrumb({ message: string, level: severity });
      return;
    }
    hub.captureMessage(string, severity);
  }

  /** @inheritdoc */
  debug(value) {
    return this.capture(ErrorLevel.Debug, value);
  }

  /** @inheritdoc */
  error(value) {
    return this.capture(ErrorLevel.Error, value);
  }

  /** @inheritdoc */
  info(value) {
    return this.capture(ErrorLevel.Info, value);
  }

  /**
   * Určí, jestli se má daná úroveň důležitosti zalogovat jako Sentry
   * breadcrumb, která se samostatně neodesílá na server, nebo jako plnohodnotná
   * chyba, která se ihned odešle na server i s předchozími breadcrumb.
   * @param {ErrorLevel} level
   * @private
   * @returns {boolean}
   */
  isBreadcrumb(level) {
    return this.errorLevelFactory.compare(level, this.captureErrorLevel) === -1;
  }

  /** @inheritdoc */
  log(value) {
    return this.capture(ErrorLevel.Log, value);
  }

  /**
   * @param {string} [buildId]
   * @private
   * @returns {undefined}
   */
  setBuildId(buildId) {
    if (isNil(buildId)) {
      return;
    }
    this.hub.setExtra('buildId', buildId);
  }

  /**
   * @param {string} [deviceId]
   * @private
   * @returns {undefined}
   */
  setDeviceId(deviceId) {
    if (isNil(deviceId)) {
      return;
    }
    this.hub.setUser({ id: deviceId });
  }

  /**
   * @param {EnvEnum} [envEnum]
   * @private
   * @returns {undefined}
   */
  setEnvEnum(envEnum) {
    if (isNil(envEnum)) {
      return;
    }
    // TODO Změna environment po inicializaci není oficiálně podporována.
    // - https://github.com/getsentry/sentry-javascript/issues/2388
    // eslint-disable-next-line no-underscore-dangle
    this.client._options.environment = EnvUtil.generate(envEnum);
  }

  /** @inheritdoc */
  setInfo(loggerInfo) {
    this.setBuildId(loggerInfo.buildId);
    this.setDeviceId(loggerInfo.deviceId);
    this.setEnvEnum(loggerInfo.envEnum);
    this.setManufacturer(loggerInfo.manufacturer);
    this.setModelName(loggerInfo.modelName);
    this.setOnline(loggerInfo.online);
    this.setRuntimePlatform(loggerInfo.runtimePlatform);
    this.setUpdatePlatform(loggerInfo.updatePlatform);
    this.setUpdateUrl(loggerInfo.updateUrl);
    this.setVersion(loggerInfo.version);
  }

  /**
   * @param {string} [manufacturer]
   * @private
   * @returns {undefined}
   */
  setManufacturer(manufacturer) {
    if (isNil(manufacturer)) {
      return;
    }
    this.hub.setExtra('manufacturer', manufacturer);
  }

  /**
   * @param {string} [modelName]
   * @private
   * @returns {undefined}
   */
  setModelName(modelName) {
    if (isNil(modelName)) {
      return;
    }
    this.hub.setExtra('modelName', modelName);
  }

  /**
   * @param {boolean} [online]
   * @private
   * @returns {undefined}
   */
  setOnline(online) {
    if (isNil(online)) {
      return;
    }
    this.hub.setExtra('online', online);
  }

  /**
   * @param {Platform} [runtimePlatform]
   * @private
   * @returns {undefined}
   */
  setRuntimePlatform(runtimePlatform) {
    if (isNil(runtimePlatform)) {
      return;
    }
    this.hub.setExtra(
      'runtimePlatform',
      PlatformUtil.generate(runtimePlatform)
    );
  }

  /**
   * @param {UpdatePlatform} [updatePlatform]
   * @private
   * @returns {undefined}
   */
  setUpdatePlatform(updatePlatform) {
    if (isNil(updatePlatform)) {
      return;
    }
    this.hub.setExtra(
      'updatePlatform',
      UpdatePlatformUtil.generate(updatePlatform)
    );
  }

  /**
   * @param {Url} [updateUrl]
   * @private
   * @returns {undefined}
   */
  setUpdateUrl(updateUrl) {
    if (isNil(updateUrl)) {
      return;
    }
    this.hub.setExtra('updateUrl', updateUrl);
  }

  /**
   * @param {string} [version]
   * @private
   * @returns {undefined}
   */
  setVersion(version) {
    if (isNil(version)) {
      return;
    }
    // TODO Změna release po inicializaci není oficiálně podporována.
    // - https://github.com/getsentry/sentry-javascript/issues/2387
    // eslint-disable-next-line no-underscore-dangle
    this.client._options.release = version;
  }

  /** @inheritdoc */
  warn(value) {
    return this.capture(ErrorLevel.Warning, value);
  }
}

export default SentryLogger;
